// Group Content — reusable content groups consumed by homepage slots (and
// future page slots). Mirrors packages/db/src/schema/group-content.ts but
// duplicated to avoid making @news-cms/shared depend on @news-cms/db.

export const GROUP_CONTENT_TYPES = ['article', 'video', 'url', 'streaming', 'customized'] as const;
export type GroupContentType = (typeof GROUP_CONTENT_TYPES)[number];

export const GROUP_CONTENT_MODES = ['auto', 'manual'] as const;
export type GroupContentMode = (typeof GROUP_CONTENT_MODES)[number];

export const GROUP_CONTENT_ARTICLE_SORTS = ['latest', 'popular'] as const;
export type GroupContentArticleSort = (typeof GROUP_CONTENT_ARTICLE_SORTS)[number];

// 'latest' = video publish date desc; 'playlist' = the playlist's own
// position order (matches the admin playlist page + the YouTube playlist).
export const GROUP_CONTENT_VIDEO_SORTS = ['latest', 'playlist'] as const;
export type GroupContentVideoSort = (typeof GROUP_CONTENT_VIDEO_SORTS)[number];

export const GROUP_CONTENT_MAX_COUNT = 20;
export const GROUP_CONTENT_MIN_COUNT = 1;

/* ---------- Config (type+mode discriminated) ---------- */

export interface ArticleAutoConfig {
  kind: 'article-auto';
  /** Category slug (not id) — matches the existing /homepage auto pattern.
   *  null = "ทุกหมวด" (no filter). */
  categorySlug: string | null;
  sort: GroupContentArticleSort;
}

export interface ArticleManualConfig {
  kind: 'article-manual';
  items: { postId: string }[];
}

export interface VideoAutoConfig {
  kind: 'video-auto';
  playlistId: string | null;
  sort: GroupContentVideoSort;
}

export interface VideoManualConfig {
  kind: 'video-manual';
  items: { videoId: string }[];
}

export interface UrlManualItem {
  title: string;
  url: string;
  imageUrl: string | null;
  summary: string | null;
  /** HTML embed snippet (iframe etc.) — public renderer decides whether to
   *  embed inline or render the link card. Null = pure link card. */
  embed: string | null;
}

export interface UrlManualConfig {
  kind: 'url-manual';
  items: UrlManualItem[];
}

/** Streaming items are just URLs — public renderer decides how to embed
 *  (HLS/MPD/YouTube live etc.). Editor-authored, no picker dialog. */
export interface StreamingManualItem {
  url: string;
}

export interface StreamingManualConfig {
  kind: 'streaming-manual';
  items: StreamingManualItem[];
}

/** Customized = mixed-type list. Each item carries its own `itemType`
 *  discriminator so admin can interleave articles, videos, link cards,
 *  and live streams in a single curated group. Article/video items
 *  reference entities by id (same as their dedicated types); url +
 *  streaming items carry a minimal inline payload (title+url for url,
 *  url-only for streaming). Manual-only — no auto source. */
export type CustomizedManualItem =
  | { itemType: 'article'; postId: string }
  | { itemType: 'video'; videoId: string }
  | { itemType: 'url'; title: string; url: string }
  | { itemType: 'streaming'; url: string };

export interface CustomizedManualConfig {
  kind: 'customized-manual';
  items: CustomizedManualItem[];
}

export type GroupContentConfig =
  | ArticleAutoConfig
  | ArticleManualConfig
  | VideoAutoConfig
  | VideoManualConfig
  | UrlManualConfig
  | StreamingManualConfig
  | CustomizedManualConfig;

/* ---------- Admin shapes ---------- */

export interface GroupContentSummary {
  id: string;
  name: string;
  slug: string;
  type: GroupContentType;
  mode: GroupContentMode;
  maxCount: number;
  /** Number of currently-configured items (manual: items.length, auto: maxCount). */
  itemCount: number;
  updatedAt: string;
  createdAt: string;
}

export interface GroupContentDetail extends GroupContentSummary {
  config: GroupContentConfig;
  createdById: string | null;
  updatedById: string | null;
}

export interface GroupContentListResponse {
  items: GroupContentSummary[];
  total: number;
  page: number;
  pageSize: number;
}

/* ---------- Hydrated items (public response) ---------- */

export interface HydratedArticleItem {
  type: 'article';
  id: string;
  slug: string;
  title: string;
  summary: string | null;
  coverImageUrl: string | null;
  publishedAt: string | null;
  categorySlug: string | null;
  categoryName: string | null;
}

export interface HydratedVideoItem {
  type: 'video';
  id: string;
  youtubeId: string;
  title: string;
  description: string | null;
  thumbnailUrl: string;
  channelTitle: string | null;
  durationSeconds: number | null;
  publishedAt: string | null;
  playlistId: string;
}

export interface HydratedUrlItem {
  type: 'url';
  title: string;
  url: string;
  imageUrl: string | null;
  summary: string | null;
  embed: string | null;
}

export interface HydratedStreamingItem {
  type: 'streaming';
  url: string;
}

export type HydratedGroupItem =
  | HydratedArticleItem
  | HydratedVideoItem
  | HydratedUrlItem
  | HydratedStreamingItem;
